import styled, { keyframes, css } from 'styled-components';

// ── paleta base (herda do design system de conceitos) ──────────────
const TEAL = 'var(--teal)';
const TEAL_TEXT = 'var(--teal-text, var(--teal))';
const TEAL_LIGHT = 'var(--teal-light)';
const TEAL_SOFT = 'var(--teal-soft)';
const GRAY_BG = 'var(--bg)';
const GRAY_LINE = 'var(--line)';
const GRAY_TEXT = 'var(--text-muted)';
const GREEN = 'var(--green)';
const AMBER = 'var(--amber, #d97706)';

// ── acentos próprios desta página ──────────────────────────────────
const CORAL = 'var(--coral)';
const TEAL_GLOW = 'var(--teal-glow)';

const bp = { desktop: '1024px', tablet: '768px', mobile: '540px', small: '380px' };

const expand = keyframes`
  from { opacity: 0; transform: translateY(-6px); max-height: 0; }
  to   { opacity: 1; transform: translateY(0);    max-height: 9999px; }
`;
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(224, 122, 95, 0.5); }
  50%      { box-shadow: 0 0 0 6px rgba(224, 122, 95, 0); }
`;
const popIn = keyframes`
  0%   { opacity: 0; transform: scale(0.6); }
  60%  { transform: scale(1.08); }
  100% { opacity: 1; transform: scale(1); }
`;
const growBar = keyframes`from { width: 0; }`;

/* ── Layout base ───────────────────────────────────────────────── */
export const Container = styled.div`
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  box-sizing: border-box;
  background:
    radial-gradient(1200px 400px at 50% -120px, var(--bg-glow) 0%, transparent 70%),
    ${GRAY_BG};
  padding: 16px;
  display: flex;
  justify-content: center;
  @media (max-width: ${bp.tablet}) { padding: 12px; }
  @media (max-width: ${bp.mobile}) { padding: 8px; }
`;

export const ContainerDivs = styled.div`
  width: 100%;
  max-width: 960px;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;
  @media (max-width: ${bp.mobile}) { gap: 10px; }
`;

/* ── Header com faixa de progresso do ano ──────────────────────── */
export const Header = styled.div`
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  padding: 22px 24px 20px;
  background: linear-gradient(135deg, ${TEAL} 0%, ${TEAL_LIGHT} 100%);
  color: #fff;
  border-radius: 18px;
  box-shadow: 0 8px 24px ${TEAL_GLOW};
  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }
  &::before { width: 180px; height: 180px; top: -70px; right: -40px; }
  &::after  { width: 120px; height: 120px; bottom: -60px; right: 90px; }
  @media (max-width: ${bp.mobile}) { padding: 18px 18px 16px; border-radius: 16px; }
`;

export const HeaderTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  @media (max-width: ${bp.mobile}) { flex-direction: column; align-items: stretch; }
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  gap: 10px;
  @media (max-width: ${bp.mobile}) { font-size: 18px; }
  @media (max-width: ${bp.small}) { font-size: 17px; }
`;

export const Subtitle = styled.span`
  font-size: 13px;
  opacity: 0.85;
  font-weight: 500;
  @media (max-width: ${bp.mobile}) { font-size: 12px; }
`;

export const HeaderStats = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  @media (max-width: ${bp.mobile}) { justify-content: stretch; }
`;

export const StatPill = styled.div`
  background: rgba(255, 255, 255, 0.13);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 12px;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 92px;
  backdrop-filter: blur(4px);
  strong { font-size: 19px; font-weight: 800; line-height: 1.1; }
  span { font-size: 10px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.6px; }
  @media (max-width: ${bp.mobile}) { flex: 1; }
`;

export const YearProgress = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  .labels {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    opacity: 0.85;
    font-weight: 600;
    letter-spacing: 0.3px;
  }
  .track {
    height: 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
    overflow: hidden;
  }
  .fill {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #ffd9a0, ${CORAL});
    width: ${({ $pct }) => Math.min(100, Math.max(0, $pct || 0))}%;
    animation: ${growBar} 0.9s ease;
    transition: width 0.4s ease;
  }
`;

/* ── Aviso (regime) ──────────────────────────────────────────── */
export const InfoBanner = styled.div`
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-left: 4px solid ${TEAL_LIGHT};
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  h2 { margin: 0; font-size: 15px; font-weight: 700; color: ${TEAL_TEXT}; display: flex; align-items: center; gap: 8px; }
  span { font-size: 12px; color: ${GRAY_TEXT}; line-height: 1.5; }
  strong { color: ${TEAL_TEXT}; }
`;

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 2px 0;
  color: ${GRAY_TEXT};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  &::after { content: ''; flex: 1; height: 1px; background: ${GRAY_LINE}; }
`;

/* ── Timeline ──────────────────────────────────────────────────── */
export const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const TimelineItem = styled.div`
  position: relative;
  padding-left: 38px;
  padding-bottom: 14px;
  &::before {
    content: '';
    position: absolute;
    left: 15px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${GRAY_LINE};
  }
  &:first-child::before { top: 26px; }
  &:last-child::before { bottom: calc(100% - 26px); }
  @media (max-width: ${bp.mobile}) {
    padding-left: 30px;
    &::before { left: 11px; }
  }
`;

export const TimelineDot = styled.span`
  position: absolute;
  left: 8px;
  top: 18px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  z-index: 1;
  border: 3px solid #fff;
  box-shadow: 0 0 0 1px ${GRAY_LINE};
  ${({ $status }) => $status === 'active' && css`
    background: ${CORAL};
    box-shadow: 0 0 0 1px ${CORAL};
    animation: ${pulse} 2s infinite;
  `}
  ${({ $status }) => $status === 'done' && css`background: ${GREEN}; box-shadow: 0 0 0 1px ${GREEN};`}
  ${({ $status }) => $status === 'future' && css`background: #c4ced3;`}
  ${({ $status }) => $status === 'empty' && css`background: var(--surface); box-shadow: 0 0 0 1px ${GRAY_LINE};`}
  @media (max-width: ${bp.mobile}) { left: 4px; }
`;

/* ── Card de período ───────────────────────────────────────────── */
export const PeriodCard = styled.div`
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  overflow: hidden;
  transition: box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease;
  ${({ $status }) => $status === 'active' && css`
    border-color: #f0c9bd;
    box-shadow: 0 4px 16px rgba(224, 122, 95, 0.12);
  `}
  &:hover { box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07); transform: translateY(-1px); }
  @media (max-width: ${bp.mobile}) { border-radius: 12px; }
`;

export const PeriodHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: ${({ $empty }) => ($empty ? '#fbfcfd' : TEAL_SOFT)};
  border-bottom: 1px solid ${GRAY_LINE};
  @media (max-width: ${bp.mobile}) { padding: 12px 14px; flex-wrap: wrap; gap: 8px; }
`;

export const PeriodTitle = styled.span`
  font-weight: 700;
  font-size: 15px;
  color: ${TEAL_TEXT};
  display: flex;
  align-items: center;
  gap: 10px;
  @media (max-width: ${bp.mobile}) { font-size: 14px; }
`;

export const StatusChip = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  letter-spacing: 0.3px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  ${({ $status }) => $status === 'active' && css`background: var(--red-soft); color: ${CORAL};`}
  ${({ $status }) => $status === 'done' && css`background: var(--green-soft); color: ${GREEN};`}
  ${({ $status }) => $status === 'future' && css`background: var(--surface-2); color: ${GRAY_TEXT};`}
`;

export const Badge = styled.span`
  background: ${TEAL};
  color: #fff;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
`;

export const PeriodBody = styled.div`
  padding: 16px 18px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px 18px;
  @media (max-width: ${bp.mobile}) { padding: 14px; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: ${bp.small}) { grid-template-columns: 1fr; }
`;

export const DateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  label {
    font-size: 11px; font-weight: 600; color: ${GRAY_TEXT};
    text-transform: uppercase; letter-spacing: 0.3px;
    display: flex; align-items: center; gap: 5px;
  }
  strong { font-size: 15px; color: ${TEAL_TEXT}; font-weight: 700; }
`;

export const GradeField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  label { font-size: 11px; font-weight: 600; color: ${GRAY_TEXT}; text-transform: uppercase; letter-spacing: 0.3px; }
  strong { font-size: 15px; font-weight: 700; color: ${({ $variant }) => ($variant === 'media' ? 'var(--blue, #3b82f6)' : GREEN)}; }
`;

export const PeriodFooter = styled.div`
  padding: 0 18px 16px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  @media (max-width: ${bp.mobile}) { padding: 0 14px 14px; flex-direction: column; }
`;

export const EmptyPeriod = styled.div`
  padding: 22px 18px;
  text-align: center;
  color: ${GRAY_TEXT};
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  .em { font-size: 26px; opacity: 0.5; }
`;

/* ── Botões ────────────────────────────────────────────────────── */
const ActionBase = styled.button`
  border: none;
  padding: 9px 14px;
  border-radius: 9px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: filter 0.12s ease, transform 0.12s ease, box-shadow 0.12s ease;
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover { filter: brightness(0.96); }
  &:active { transform: translateY(1px); }
  &:focus-visible { outline: 2px solid ${TEAL_LIGHT}; outline-offset: 2px; }
  &:disabled { cursor: not-allowed; opacity: 0.6; }
  @media (max-width: ${bp.mobile}) { width: 100%; min-height: 44px; font-size: 14px; }
`;

export const ViewDaysButton = styled(ActionBase)`
  background: ${TEAL_SOFT};
  color: ${TEAL_TEXT};
  border: 1px solid var(--teal-soft);
  &:hover { background: var(--teal-soft); filter: none; }
`;

export const EditButton = styled(ActionBase)`
  background: var(--teal-soft);
  color: ${TEAL_TEXT};
  border: 1px solid var(--teal-soft);
`;

export const DefineButton = styled(ActionBase)`
  background: ${TEAL};
  color: #fff;
  box-shadow: 0 2px 8px ${TEAL_GLOW};
`;

/* ── Skeleton ──────────────────────────────────────────────────── */
const skeletonBase = css`
  background: linear-gradient(90deg, var(--skel-a) 0%, var(--skel-b) 50%, var(--skel-a) 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.3s linear infinite;
  border-radius: 8px;
`;

export const SkeletonLine = styled.div`
  ${skeletonBase}
  height: ${({ $h }) => $h || '14px'};
  width: ${({ $w }) => $w || '100%'};
  margin: 4px 0;
`;

export const SkeletonCard = styled.div`
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/* ── Erro ──────────────────────────────────────────────────────── */
export const ErrorState = styled.div`
  background: var(--surface);
  border: 1px dashed ${GRAY_LINE};
  border-radius: 14px;
  padding: 44px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  .icon { font-size: 46px; }
  h2 { margin: 0; font-size: 17px; color: ${TEAL_TEXT}; }
  p { margin: 0; color: ${GRAY_TEXT}; font-size: 14px; }
`;

export const RetryButton = styled(ActionBase)`
  background: ${TEAL};
  color: #fff;
  padding: 10px 24px;
  width: auto;
  max-width: 240px;
`;

/* ── Modais ────────────────────────────────────────────────────── */
export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 30, 38, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: ${fadeIn} 0.15s ease;
  overscroll-behavior: contain;
  @media (max-width: ${bp.mobile}) { padding: 0; align-items: flex-end; }
`;

export const Modal = styled.div`
  background: var(--surface);
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
  text-align: center;
  animation: ${expand} 0.2s ease;
  @media (max-width: ${bp.mobile}) { border-radius: 18px 18px 0 0; padding: 22px 18px; max-width: 100%; }
`;

export const ModalTitle = styled.h3`
  margin: 0 0 10px;
  color: ${AMBER};
  font-size: 18px;
`;

export const ModalText = styled.p`
  margin: 0 0 20px;
  color: ${GRAY_TEXT};
  font-size: 14px;
  line-height: 1.55;
  strong { color: ${TEAL_TEXT}; }
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  @media (max-width: ${bp.small}) { flex-direction: column-reverse; & > button { width: 100%; } }
`;

export const ConfirmButton = styled(ActionBase)`
  background: ${TEAL};
  color: #fff;
  padding: 10px 18px;
  width: auto;
`;

export const CancelButton = styled(ActionBase)`
  background: var(--surface-2);
  color: ${GRAY_TEXT};
  border: 1px solid ${GRAY_LINE};
  padding: 10px 18px;
  width: auto;
`;

/* ── Modal calendário ──────────────────────────────────────────── */
export const CalendarModal = styled.div`
  background: var(--surface);
  border-radius: 16px;
  width: 100%;
  max-width: 940px;
  max-height: 88vh;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${expand} 0.22s ease;
  @media (max-width: ${bp.mobile}) { border-radius: 18px 18px 0 0; max-width: 100%; max-height: 92vh; }
`;

export const CalendarModalHeader = styled.div`
  background: linear-gradient(135deg, ${TEAL} 0%, ${TEAL_LIGHT} 100%);
  color: #fff;
  padding: 16px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  h3 { margin: 0; font-size: 16px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
  .count { background: rgba(255, 255, 255, 0.18); border-radius: 999px; padding: 5px 14px; font-size: 13px; font-weight: 700; white-space: nowrap; }
  @media (max-width: ${bp.mobile}) { padding: 14px 16px; h3 { font-size: 15px; } }
`;

export const CalendarLegend = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px 22px;
  border-bottom: 1px solid ${GRAY_LINE};
  background: ${GRAY_BG};
  flex-shrink: 0;
  flex-wrap: wrap;
  span { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: ${GRAY_TEXT}; font-weight: 600; }
  i { width: 14px; height: 14px; border-radius: 4px; display: inline-block; }
  .school { background: ${TEAL}; }
  .off { background: #f0f3f5; border: 1px solid ${GRAY_LINE}; }
  .today { background: var(--surface); border: 2px solid ${CORAL}; }
  @media (max-width: ${bp.mobile}) { padding: 8px 16px; gap: 12px; }
`;

export const CalendarScroll = styled.div`
  overflow-y: auto;
  padding: 18px 22px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
  @media (max-width: ${bp.tablet}) { grid-template-columns: 1fr; }
  @media (max-width: ${bp.mobile}) { padding: 14px 16px; gap: 14px; }
`;

export const MonthBlock = styled.div`
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  padding: 14px;
  transition: box-shadow 0.15s ease;
  &:hover { box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05); }
`;

export const MonthName = styled.h4`
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 700;
  color: ${TEAL_TEXT};
  text-transform: capitalize;
  .pill { font-size: 10px; font-weight: 700; color: ${TEAL_LIGHT}; background: ${TEAL_SOFT}; border-radius: 999px; padding: 2px 9px; text-transform: none; letter-spacing: 0.2px; }
`;

export const WeekHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 6px;
  span { text-align: center; font-size: 10px; font-weight: 700; color: ${GRAY_TEXT}; text-transform: uppercase; letter-spacing: 0.3px; }
`;

export const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

export const DayCell = styled.div`
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  font-size: 12px;
  position: relative;
  transition: transform 0.1s ease, filter 0.1s ease;
  ${({ $school }) => $school
    ? css`
        background: ${TEAL};
        color: #fff;
        font-weight: 700;
        box-shadow: 0 2px 5px ${TEAL_GLOW};
        animation: ${popIn} 0.3s ease backwards;
        cursor: default;
        &:hover { transform: translateY(-2px) scale(1.04); filter: brightness(1.08); }
      `
    : css`
        background: #f5f7f9;
        color: #b3bdc2;
        font-weight: 500;
      `}
  ${({ $today }) => $today && css`outline: 2px solid ${CORAL}; outline-offset: -2px;`}
  ${({ $empty }) => $empty && css`background: transparent; box-shadow: none; &:hover { transform: none; }`}
`;

export const CalendarFooter = styled.div`
  flex-shrink: 0;
  padding: 12px 22px;
  border-top: 1px solid ${GRAY_LINE};
  display: flex;
  justify-content: center;
  background: var(--surface);
  @media (max-width: ${bp.mobile}) { padding: 12px 16px; }
`;

export const CloseButton = styled(ActionBase)`
  background: ${TEAL};
  color: #fff;
  padding: 10px 30px;
  width: auto;
  @media (max-width: ${bp.mobile}) { width: 100%; }
`;

export const EmptyCalendar = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 44px 20px;
  color: ${GRAY_TEXT};
  font-size: 14px;
`;
